import React, { useState, useEffect } from 'react';
import { signInWithGoogle, getStoredAuthSession, isAuthorizedUser } from '../../services/auth.js';
import { ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';

export function LoginBarrier({ onAuthenticated }) {
  const [checking, setChecking] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Check if session exists in storage or URL
    const unauthorizedEmail = sessionStorage.getItem('sdr_auth_unauthorized_email');
    if (unauthorizedEmail) {
      sessionStorage.removeItem('sdr_auth_unauthorized_email');
      setErrorMsg(`บัญชี ${unauthorizedEmail} ไม่ได้รับอนุญาตให้เข้าใช้งาน กรุณาใช้อีเมลสถาบัน (@student.mahidol.edu หรือ @mahidol.ac.th)`);
      setChecking(false);
      return;
    }

    const session = getStoredAuthSession();
    if (session?.user && isAuthorizedUser(session.user)) {
      onAuthenticated(session.user);
    } else {
      setChecking(false);
    }
  }, [onAuthenticated]);

  const handleGoogleLogin = () => {
    setChecking(true);
    setErrorMsg('');
    try {
      signInWithGoogle();
      // Failsafe timeout to prevent infinite spinner if navigation is delayed
      setTimeout(() => setChecking(false), 8000);
    } catch (err) {
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อกับ Google OAuth');
      setChecking(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-slate-900 select-none">
      {/* Ambient background glows */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-600/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-cyan-600/25 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 text-center transition-all">
        {/* SDR Logo */}
        <div className="mx-auto mb-5 w-20 h-20 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shadow-inner overflow-hidden">
          <img
            src="sdr-logo.png"
            alt="SDR Club Logo"
            className="w-16 h-16 object-contain"
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = '1';
                e.currentTarget.src = 'assets/sdr-logo.png';
              } else {
                e.currentTarget.src = 'sdr-logo-favicon.png';
              }
            }}
          />
        </div>

        <div className="inline-block px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200/60 text-indigo-700 text-xs font-semibold mb-3 tracking-wide">
          SDR Club • Mahidol University
        </div>

        <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight mb-2">
          SDR Stock Manager
        </h1>
        <p className="text-sm text-slate-600 mb-6 font-semibold">
          ระบบจัดการคลังพัสดุและยืม-คืนอุปกรณ์ ชมรม SDR
        </p>

        {checking ? (
          <div className="py-6 flex flex-col items-center justify-center gap-3 text-slate-600 font-medium text-sm">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            <span>กำลังตรวจสอบสิทธิ์การเข้าสู่ระบบ...</span>
          </div>
        ) : (
          <>
            {errorMsg && (
              <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-medium flex items-start gap-2.5 text-left leading-relaxed">
                <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-500 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl bg-white border border-slate-300 shadow-sm hover:shadow-md hover:bg-slate-50 active:scale-[0.98] transition-all text-slate-800 font-bold text-sm cursor-pointer"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
              </svg>
              <span>เข้าสู่ระบบด้วย Google Account</span>
            </button>
          </>
        )}

        <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-600 font-semibold">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <span>รองรับบัญชี @student.mahidol.edu / .ac.th</span>
        </div>
      </div>
    </div>
  );
}
